import { PaymentRequisiteToType } from '@/entities/paymentRequisit'
import {
  formatBankAccount,
  formatCardNumber,
  RequisiteModel,
  UpdateRequisite,
  useEditRequisiteForm,
  useRequisiteOptions,
} from '@/features/requisites'
import CardIcon from '@/shared/assets/icons/card.svg?react'
import ArrowUpIcon from '@/shared/assets/icons/detail/arrow-up.svg?react'
import AccountIcon from '@/shared/assets/icons/document.svg?react'
import SimCardIcon from '@/shared/assets/icons/simСard.svg?react'
import TrashIcon from '@/shared/assets/icons/trash.svg?react'
import { BaseButton, BaseDialog, BaseInput, BaseSelect, BaseTooltip } from '@/shared/components'
import { Plus } from 'lucide-react'
import { useEffect, useMemo } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { PaymentMethodLimits } from '@/features/requisites/dialogs/dialogEditRequisit/PaymentMethodLimits.tsx'

type Props = {
  modalVisible: boolean
  setModalVisible: (value: boolean) => void
  onSubmit: (data: UpdateRequisite) => void
  onDelete: () => void
  onDeleteOptionalCard?: (id: string, name: string) => void
  requisitData?: RequisiteModel
  isLoading?: boolean
}

export const DialogEditRequisit = ({
                                     modalVisible,
                                     setModalVisible,
                                     onSubmit,
                                     onDelete,
                                     onDeleteOptionalCard,
                                     requisitData,
                                     isLoading,
                                   }: Props) => {
  const {
    register,
    handleSubmit,
    control,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<RequisiteModel>({
    defaultValues: requisitData,
  })

  const { optionsBanks, optionsDevice, optionsFarm } = useRequisiteOptions(modalVisible)

  // Используем обновленный хук
  const {
    showAdditionalFields,
    additionalCards,
    formattedPhone,
    formattedAccount,
    formattedMainCard,
    phoneError,
    accountError,
    mainCardError,
    additionalCardsErrors,
    handlePhoneChange,
    handleAccountChange,
    handleMainCardChange,
    handleAdditionalCardChange,
    addCard,
    removeCard,
    validateForm,
    resetForm,
    getPaymentRequisitProfiles,
    requisiteTypes,
  } = useEditRequisiteForm({ watch, setValue })

  const farmId = watch('farmId')
  const isFarmSelected = Boolean(farmId)

  // Получаем конфигурацию типов реквизитов для выбранного банка
  const { sbpType, accountType, cardType, sbpLinkType, configs } = requisiteTypes

  // true - если есть карта в качестве реквизита
  const mainCardLocked = useMemo(() => {
    const profiles = requisitData?.paymentRequisitProfiles ?? []

    return profiles.some(
      p =>
        p.requisitType === PaymentRequisiteToType.CardNumber &&
        String(p.value ?? '').trim() !== ''
    )
  }, [requisitData?.id])

  const phoneLocked = useMemo(() => {
    const profiles = requisitData?.paymentRequisitProfiles ?? []

    return profiles.some(
      p =>
        p.requisitType === PaymentRequisiteToType.SbpPhoneNumber &&
        String(p.value ?? '').trim() !== ''
    )
  }, [requisitData?.id])

  const accountNumberLocked = useMemo(() => {
    const profiles = requisitData?.paymentRequisitProfiles ?? []

    return profiles.some(
      p =>
        p.requisitType === PaymentRequisiteToType.AccountNumber &&
        String(p.value ?? '').trim() !== ''
    )
  }, [requisitData?.id])

  // Инициализация данных формы
  useEffect(() => {
    if (!requisitData?.bankId) return

    const profiles = requisitData.paymentRequisitProfiles ?? []
    if (!profiles.length) return

    let hasMain = false;

    for (const profile of profiles) {
      switch (profile.requisitType) {
        case PaymentRequisiteToType.CardNumber:
          if (!hasMain) {
            handleMainCardChange(profile.value)
            hasMain = true
          } else {
            addCard(profile.value, profile.id)
          }
          break
        case PaymentRequisiteToType.SbpPhoneNumber:
          handlePhoneChange(profile.value)
          break
        case PaymentRequisiteToType.AccountNumber:
          handleAccountChange(profile.value)
          break
      }
    }
  }, [
    requisitData?.id, // ← запускаем только при загрузке другой записи
  ])

  const lockedAdditionalCardIds = useMemo(() => {
    const profiles = requisitData?.paymentRequisitProfiles ?? []
    // все сохранённые карты (кроме главной — она рендерится отдельно и уже лочится mainCardLocked)
    return new Set(
      profiles
        .filter(
          p =>
            p.requisitType === PaymentRequisiteToType.CardNumber &&
            String(p.value ?? '').trim() !== ''
        )
        .map(p => p.id)
    )
  }, [requisitData?.id])

  const handleClose = () => {
    reset()
    resetForm()
    setModalVisible(false)
  }

  // Обработчик отправки формы
  const onFormSubmit = (data: RequisiteModel) => {
    if (!validateForm()) {
      return
    }

    // Получаем актуальные профили реквизитов
    const paymentRequisitProfiles = getPaymentRequisitProfiles()

    // Преобразуем данные для отправки
    const convertedData: UpdateRequisite = {
      ...data,
      maxDailyLimit: Number(data.maxDailyLimit),
      minTimeoutBetweenDealsInMinutes: Number(data.minTimeoutBetweenDealsInMinutes),
      minDealAmount: Number(data.minDealAmount),
      maxDealAmount: Number(data.maxDealAmount),
      currency: 643,
      paymentRequisitProfiles: paymentRequisitProfiles,
    }

    onSubmit(convertedData)
  }

  // Функция для обработки удаления карты
  const handleCardDelete = (index: number, card: { id: string; value: string }) => {
    const isTemporaryCard = !card.id || card.id.startsWith('temp-')
    if (isTemporaryCard) {
      removeCard(index)
    } else {
      if (onDeleteOptionalCard) {
        const lastFourDigits = card.value.replace(/\D/g, '').slice(-4) || '****'
        onDeleteOptionalCard(card.id, `*${lastFourDigits}`)
      }
    }
  }

  // Функция получения иконки для типа реквизита
  const getIconForRequisiteType = (type: PaymentRequisiteToType) => {
    switch (type) {
      case PaymentRequisiteToType.CardNumber:
        return <CardIcon className="text-text-secondary" />
      case PaymentRequisiteToType.SbpPhoneNumber:
        return <SimCardIcon className="text-text-secondary" />
      case PaymentRequisiteToType.AccountNumber:
        return <AccountIcon className="text-text-secondary" />
      default:
        return null
    }
  }

  // Рендеринг полей формы в зависимости от типа банка
  const renderRequisiteFields = () => {
    // Если не выбран банк, не отображаем поля реквизитов
    if (!showAdditionalFields) return null

    return (
      <div className="relative flex flex-col gap-4">
        {/* Рендерим основной реквизит */}
        {sbpType === PaymentRequisiteToType.SbpPhoneNumber && (
          <BaseInput
            label={configs[sbpType].label}
            placeholder={configs[sbpType].placeholder}
            variant="secondary"
            value={formattedPhone}
            onChange={(e) => handlePhoneChange(e.target.value)}
            leftIcon={getIconForRequisiteType(sbpType)}
            error={phoneError}
            readOnly={phoneLocked}
          />
        )}

        {sbpType === PaymentRequisiteToType.CardNumber && (
          <BaseInput
            label={configs[sbpType].label}
            placeholder={configs[sbpType].placeholder}
            variant="secondary"
            value={formattedMainCard}
            onChange={(e) => handleMainCardChange(e.target.value)}
            leftIcon={getIconForRequisiteType(sbpType)}
            error={mainCardError}
            readOnly={mainCardLocked}
          />
        )}

        {cardType === PaymentRequisiteToType.CardNumber && (
          <BaseInput
            label={configs[cardType].label}
            placeholder={configs[cardType].placeholder}
            variant="secondary"
            value={formattedMainCard}
            onChange={(e) => handleMainCardChange(e.target.value)}
            leftIcon={getIconForRequisiteType(cardType)}
            error={mainCardError}
            readOnly={mainCardLocked}
          />
        )}

        {/* Рендерим вторичный реквизит */}
        {accountType && (
          <div className="flex gap-[10px] w-full">
            {accountType === PaymentRequisiteToType.AccountNumber && <ArrowUpIcon />}
            <BaseInput
              label={configs[accountType].label}
              placeholder={configs[accountType].placeholder}
              variant="secondary"
              readOnly={accountType === PaymentRequisiteToType.SbpPhoneNumber && phoneLocked ||
                accountType === PaymentRequisiteToType.AccountNumber && accountNumberLocked}
              value={
                accountType === PaymentRequisiteToType.SbpPhoneNumber
                  ? formattedPhone
                  : accountType === PaymentRequisiteToType.AccountNumber
                    ? formatBankAccount(formattedAccount).formatted
                    : formattedMainCard
              }
              onChange={(e) => {
                if (accountType === PaymentRequisiteToType.SbpPhoneNumber) {
                  handlePhoneChange(e.target.value)
                } else if (accountType === PaymentRequisiteToType.AccountNumber) {
                  handleAccountChange(e.target.value)
                } else {
                  handleMainCardChange(e.target.value)
                }
              }}
              leftIcon={getIconForRequisiteType(accountType)}
              error={
                accountType === PaymentRequisiteToType.SbpPhoneNumber
                  ? phoneError
                  : accountType === PaymentRequisiteToType.AccountNumber
                    ? accountError
                    : mainCardError
              }
              className="w-full"
            />
          </div>
        )}

        {/* Рендерим дополнительные карты */}
        {additionalCards.map((card, index) => (
          <BaseInput
            key={card.id}
            label={configs[PaymentRequisiteToType.CardNumber].label}
            placeholder={configs[PaymentRequisiteToType.CardNumber].placeholder}
            variant="secondary"
            value={formatCardNumber(card.value).formatted}
            readOnly={lockedAdditionalCardIds.has(card.id)}
            onChange={(e) => handleAdditionalCardChange(e.target.value, index)}
            leftIcon={getIconForRequisiteType(PaymentRequisiteToType.CardNumber)}
            error={additionalCardsErrors[index]}
            rightIcon={
              <BaseButton variant="ghost" size="icon" onClick={() => handleCardDelete(index, card)}>
                <TrashIcon className="text-error" />
              </BaseButton>
            }
          />
        ))}

        {/* Кнопка добавления карты - показываем только если тип карты есть в дополнительных */}
        {cardType === PaymentRequisiteToType.CardNumber && (
          <BaseButton
            onClick={() => addCard()}
            variant="ghost"
            iconPosition="left"
            icon={<Plus className="text-primary dark:text-primary-dark" />}
            className="disabled:opacity-40 text-primary dark:text-primary-dark"
          >
            Добавить карту
          </BaseButton>
        )}
      </div>
    )
  }

  return (
    <BaseDialog
      isVisible={modalVisible}
      onClose={handleClose}
      onAction={handleSubmit(onFormSubmit)}
      onDelete={onDelete}
      title="Редактирование реквизита"
      actionButtonName="Сохранить и выйти"
      isLoading={isLoading}
    >
      <div className="max-h-[600px] overflow-y-auto scrollbar-hide">
        <form onSubmit={handleSubmit(onFormSubmit)} className="flex flex-col gap-y-4 p-0.5">
          <Controller
            name="deviceId"
            control={control}
            rules={{ required: 'Необходимо выбрать устройство' }}
            render={({ field, fieldState }) => (
              <BaseSelect
                {...field}
                label="Устройство"
                placeholder="Выберите"
                options={optionsDevice}
                error={fieldState.error?.message}
                key={optionsDevice.length}
              />
            )}
          />

          <Controller
            name="bankId"
            control={control}
            rules={{ required: 'Необходимо выбрать банк' }}
            render={({ field, fieldState }) => (
              <BaseSelect
                {...field}
                key={optionsBanks.length}
                label="Банк"
                placeholder="Выберите"
                options={optionsBanks}
                error={fieldState.error?.message}
                disabled={true}
              />
            )}
          />

          <div className="gap-4 grid grid-cols-3">
            <BaseInput
              label="Фамилия"
              placeholder="Ввести"
              variant="secondary"
              readOnly={true}
              error={errors.personSurname?.message}
              {...register('personSurname', { required: 'Обязательное поле' })}
            />
            <BaseInput
              label="Имя"
              placeholder="Ввести"
              variant="secondary"
              readOnly={true}
              error={errors.personName?.message}
              {...register('personName', { required: 'Обязательное поле' })}
            />
            <BaseInput
              label="Отчество"
              placeholder="Ввести"
              variant="secondary"
              readOnly={true}
              {...register('personMiddlename')}
            />
          </div>

          <div className="flex items-start gap-3 px-3 py-4 border rounded-lg">
            <BaseTooltip text="Подробнее" />
            <span className="text-text-secondary text-sm">
              Убедитесь, что правильно привязали реквизит. После 10 отказов реквизит будет
              остановлен.
            </span>
          </div>

          {/* Динамический рендеринг полей для реквизитов */}
          {renderRequisiteFields()}

          {/* Поле для Озон чаевые */}
          {sbpLinkType === PaymentRequisiteToType.SbpPaymentLink && (
            <BaseInput
              label={configs[sbpLinkType].label}
              placeholder={configs[sbpLinkType].placeholder}
              variant="secondary"
              {...register('sbpPaymentLink')}
            />
          )}

          <Controller
            name="farmId"
            control={control}
            render={({ field }) => (
              <BaseSelect
                {...field}
                label="Прикрепить к ферме"
                placeholder="Выберите"
                options={optionsFarm}
                key={optionsFarm.length}
              />
            )}
          />
          <BaseInput
            label="Суточный лимит"
            placeholder="2 000 000 RUB"
            variant="secondary"
            type="number"
            disabled={isFarmSelected}
            error={errors.maxDailyLimit?.message}
            {...register('maxDailyLimit', {
              required: 'Обязательное поле',
              min: { value: 1, message: 'Должно быть больше 0' },
            })}
          />
          <BaseInput
            label="Интервал между сделками (мин.)"
            placeholder="0 минут"
            variant="secondary"
            type="number"
            disabled={isFarmSelected}
            error={errors.minTimeoutBetweenDealsInMinutes?.message}
            {...register('minTimeoutBetweenDealsInMinutes', {
              min: { value: 0, message: 'Должно быть положительным числом' },
            })}
          />
          <div className="gap-4 grid grid-cols-2">
            <BaseInput
              label="Мин. сумма"
              placeholder="Мин. сумма"
              variant="secondary"
              type="number"
              disabled={isFarmSelected}
              error={errors.minDealAmount?.message}
              {...register('minDealAmount', {
                required: 'Обязательное поле',
                min: { value: 1, message: 'Должно быть больше 0' },
              })}
            />
            <BaseInput
              label="Макс. сумма"
              placeholder="Макс. сумма"
              variant="secondary"
              type="number"
              disabled={isFarmSelected}
              error={errors.maxDealAmount?.message}
              {...register('maxDealAmount', {
                required: 'Обязательное поле',
                min: { value: 1, message: 'Должно быть больше 0' },
                validate: (value) => {
                  const minValue = Number(watch('minDealAmount'))
                  return !minValue || Number(value) >= minValue || 'Должно быть больше мин. суммы'
                },
              })}
            />
          </div>
          {requisitData &&
            <PaymentMethodLimits control={control} profiles={requisitData.paymentRequisitProfiles
              ? requisitData.paymentRequisitProfiles
              : []
            } />
          }

          {/* Скрытые поля для отправки формы */}
          <input type="hidden" {...register('currency')} value="643" />
        </form>
      </div>
    </BaseDialog>
  )
}